<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { debounce } from '@chatwoot/utils';
import { getFileInfo } from '@chatwoot/utils';

import SearchAPI from 'dashboard/api/search';
import { useCamelCase } from 'dashboard/composables/useTransformKeys';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { ATTACHMENT_TYPES } from 'dashboard/components-next/message/constants.js';
import NextButton from 'dashboard/components-next/button/Button.vue';
import CardLayout from 'dashboard/components-next/CardLayout.vue';
import AudioChip from 'dashboard/components-next/message/chips/Audio.vue';
import FileChip from 'dashboard/components-next/message/chips/File.vue';
import TranscribedText from 'dashboard/modules/search/components/TranscribedText.vue';

const PER_PAGE = 15;

const props = defineProps({
  filters: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const route = useRoute();

const messages = ref([]);
const page = ref(1);
const isFetching = ref(false);
const hasLoaded = ref(false);
const searchQuery = ref('');
const mediaType = ref('all');

const assigneeId = computed(() => {
  if (!props.filters.agent?.startsWith('agent:')) {
    return undefined;
  }

  return Number(props.filters.agent.split(':')[1]);
});

const baseParams = computed(() => ({
  since: props.filters.dateRange?.from || undefined,
  until: props.filters.dateRange?.to || undefined,
  inboxId: props.filters.inboxId || undefined,
  assigneeId: assigneeId.value,
  status: props.filters.status !== 'all' ? props.filters.status : undefined,
  attachmentsOnly: true,
  fileType:
    mediaType.value !== 'all' && mediaType.value !== ATTACHMENT_TYPES.FILE
      ? mediaType.value
      : undefined,
  monitorMode: true,
}));

const mediaTypeOptions = computed(() => [
  {
    key: 'all',
    label: t('MONITOR_PAGE.GALLERY.TYPES.ALL'),
    icon: 'chat',
  },
  {
    key: ATTACHMENT_TYPES.IMAGE,
    label: t('MONITOR_PAGE.GALLERY.MEDIA.IMAGE'),
    icon: 'image',
  },
  {
    key: ATTACHMENT_TYPES.VIDEO,
    label: t('MONITOR_PAGE.GALLERY.MEDIA.VIDEO'),
    icon: 'video',
  },
  {
    key: ATTACHMENT_TYPES.AUDIO,
    label: t('MONITOR_PAGE.GALLERY.MEDIA.AUDIO'),
    icon: 'music-note-2',
  },
  {
    key: ATTACHMENT_TYPES.FILE,
    label: t('MONITOR_PAGE.GALLERY.MEDIA.FILE'),
    icon: 'document',
  },
]);

const normalizedAttachmentType = attachment =>
  attachment.fileType || attachment.file_type || '';

const normalizedAttachmentDataUrl = attachment =>
  attachment.dataUrl ||
  attachment.data_url ||
  attachment.downloadUrl ||
  attachment.download_url ||
  attachment.fileUrl ||
  attachment.file_url ||
  attachment.url ||
  attachment.externalUrl ||
  attachment.external_url ||
  '';

const normalizedAttachmentThumbUrl = attachment =>
  attachment.thumbUrl ||
  attachment.thumb_url ||
  attachment.previewUrl ||
  attachment.preview_url ||
  normalizedAttachmentDataUrl(attachment);

const normalizedAttachmentTranscribedText = attachment =>
  attachment.transcribedText || attachment.transcribed_text || '';

const normalizedAttachmentFallbackTitle = attachment =>
  attachment.fallbackTitle ||
  attachment.fallback_title ||
  attachment.title ||
  attachment.fileName ||
  attachment.file_name ||
  '';

const buildMediaItem = (message, attachment) => ({
  ...attachment,
  fileType: normalizedAttachmentType(attachment),
  dataUrl: normalizedAttachmentDataUrl(attachment),
  thumbUrl: normalizedAttachmentThumbUrl(attachment),
  transcribedText: normalizedAttachmentTranscribedText(attachment),
  fallbackTitle: normalizedAttachmentFallbackTitle(attachment),
  messageId: message.messageId || message.id,
  conversationId: message.conversationId || message.conversation_id,
  createdAt: message.createdAt || message.created_at,
  senderName: message.sender?.name || t('SEARCH.BOT_LABEL'),
});

const mediaItems = computed(() =>
  messages.value.flatMap(message =>
    (message.attachments || message.files || [])
      .filter(attachment =>
        [
          ATTACHMENT_TYPES.IMAGE,
          ATTACHMENT_TYPES.VIDEO,
          ATTACHMENT_TYPES.AUDIO,
          ATTACHMENT_TYPES.FILE,
          ATTACHMENT_TYPES.FALLBACK,
          ATTACHMENT_TYPES.IG_REEL,
        ].includes(normalizedAttachmentType(attachment))
      )
      .filter(attachment => {
        if (mediaType.value === 'all') {
          return true;
        }

        if (mediaType.value === ATTACHMENT_TYPES.FILE) {
          return [ATTACHMENT_TYPES.FILE, ATTACHMENT_TYPES.FALLBACK].includes(
            normalizedAttachmentType(attachment)
          );
        }

        return normalizedAttachmentType(attachment) === mediaType.value;
      })
      .map(attachment => buildMediaItem(message, attachment))
  )
);

const showLoadMore = computed(
  () =>
    !isFetching.value &&
    messages.value.length > 0 &&
    messages.value.length === page.value * PER_PAGE
);

const normalizeRecords = records =>
  records.map(record => useCamelCase(record, { deep: true }));

const mediaTypeLabel = fileType => {
  switch (fileType) {
    case ATTACHMENT_TYPES.IMAGE:
      return t('MONITOR_PAGE.GALLERY.MEDIA.IMAGE');
    case ATTACHMENT_TYPES.VIDEO:
    case ATTACHMENT_TYPES.IG_REEL:
      return t('MONITOR_PAGE.GALLERY.MEDIA.VIDEO');
    case ATTACHMENT_TYPES.AUDIO:
      return t('MONITOR_PAGE.GALLERY.MEDIA.AUDIO');
    case ATTACHMENT_TYPES.FALLBACK:
    default:
      return t('MONITOR_PAGE.GALLERY.MEDIA.FILE');
  }
};

const mediaUrl = item =>
  frontendURL(
    `accounts/${route.params.accountId}/monitor/conversations/${item.conversationId}`,
    {
      messageId: item.messageId,
    }
  );

const isVisualMedia = fileType =>
  [
    ATTACHMENT_TYPES.IMAGE,
    ATTACHMENT_TYPES.VIDEO,
    ATTACHMENT_TYPES.IG_REEL,
  ].includes(fileType);

const fileTitle = item => {
  if (item.fallbackTitle) {
    return item.fallbackTitle;
  }

  const fileInfo = getFileInfo(item.dataUrl || '');
  return fileInfo.name || fileInfo.base || t('MONITOR_PAGE.GALLERY.MEDIA.FILE');
};

const fetchMedia = async ({ reset = false } = {}) => {
  isFetching.value = true;

  try {
    const {
      data: {
        payload: { messages: records = [] },
      },
    } = await SearchAPI.messages({
      q: searchQuery.value.trim() || undefined,
      page: page.value,
      ...baseParams.value,
    });

    const normalized = normalizeRecords(records);
    messages.value = reset ? normalized : [...messages.value, ...normalized];
  } catch {
    if (reset) {
      messages.value = [];
    }
  } finally {
    isFetching.value = false;
    hasLoaded.value = true;
  }
};

const reloadMedia = async () => {
  page.value = 1;
  messages.value = [];
  hasLoaded.value = false;
  await fetchMedia({ reset: true });
};

const loadMore = async () => {
  if (!showLoadMore.value) return;

  page.value += 1;
  await fetchMedia({ reset: false });
};

const debouncedReloadMedia = debounce(() => {
  reloadMedia();
}, 400);

watch(
  () => props.filters,
  async () => {
    await reloadMedia();
  },
  { deep: true, immediate: true }
);

watch(mediaType, async () => {
  await reloadMedia();
});

watch(
  () => searchQuery.value,
  () => {
    debouncedReloadMedia();
  }
);
</script>

<template>
  <div class="chatmigo-monitor-panel flex h-full flex-col">
    <div class="chatmigo-monitor-toolbar px-3 py-3">
      <div class="flex items-start justify-between gap-3">
        <div class="min-w-0">
          <h3 class="m-0 text-sm font-semibold tracking-tight text-n-slate-12">
            {{ t('MONITOR_PAGE.GALLERY.TITLE') }}
          </h3>
          <p class="mt-1 mb-0 text-xs leading-5 text-n-slate-10">
            {{ t('MONITOR_PAGE.GALLERY.DESCRIPTION') }}
          </p>
        </div>
        <span
          class="shrink-0 rounded-full border border-n-strong/70 bg-n-surface-1 px-2 py-1 text-[11px] font-semibold text-n-slate-11"
        >
          {{ mediaItems.length }}
        </span>
      </div>

      <div class="mt-3 flex flex-col gap-3">
        <div
          class="flex h-11 items-center gap-2 rounded-2xl border border-n-strong/70 bg-n-surface-1 px-3 shadow-sm"
        >
          <fluent-icon icon="search" size="14" class="text-n-slate-10" />
          <input
            v-model="searchQuery"
            type="search"
            class="reset-base outline-none w-full m-0 bg-transparent border-transparent shadow-none text-n-slate-12 active:border-transparent active:shadow-none hover:border-transparent hover:shadow-none focus:border-transparent focus:shadow-none placeholder:text-n-slate-10 text-sm"
            :placeholder="$t('MONITOR_PAGE.GALLERY.SEARCH_PLACEHOLDER')"
          />
        </div>

        <div class="flex flex-wrap items-center gap-2">
          <button
            v-for="option in mediaTypeOptions"
            :key="option.key"
            type="button"
            class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors"
            :class="
              mediaType === option.key
                ? 'border-n-brand bg-n-brand text-white shadow-sm'
                : 'border-n-strong/60 bg-n-surface-1 text-n-slate-11 hover:border-n-brand/30 hover:text-n-slate-12'
            "
            @click="mediaType = option.key"
          >
            <fluent-icon :icon="option.icon" size="12" />
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto px-3 py-4">
      <div
        v-if="!isFetching && hasLoaded && !mediaItems.length"
        class="chatmigo-empty-state flex flex-col items-center justify-center px-6 py-10 text-center"
      >
        <fluent-icon icon="image" size="24" class="text-n-slate-11" />
        <p class="m-2 text-n-slate-11">
          {{ t('MONITOR_PAGE.GALLERY.EMPTY') }}
        </p>
      </div>

      <div v-else class="grid grid-cols-1 xl:grid-cols-2 gap-3">
        <div
          v-for="item in mediaItems"
          :key="`${item.messageId}-${item.id}`"
        >
          <CardLayout
            layout="col"
            class="chatmigo-monitor-media-card [&>div]:items-start [&>div]:justify-start [&>div]:gap-2 [&>div]:px-3 [&>div]:py-3 hover:bg-n-slate-2 dark:hover:bg-n-solid-3"
          >
            <div
              class="flex min-h-[10rem] w-full items-center justify-center overflow-hidden rounded-2xl bg-n-alpha-2"
            >
              <img
                v-if="item.fileType === ATTACHMENT_TYPES.IMAGE && (item.thumbUrl || item.dataUrl)"
                :src="item.thumbUrl || item.dataUrl"
                class="w-full h-40 object-cover"
                :alt="mediaTypeLabel(item.fileType)"
              />
              <video
                v-else-if="
                  [ATTACHMENT_TYPES.VIDEO, ATTACHMENT_TYPES.IG_REEL].includes(
                    item.fileType
                  ) && item.dataUrl
                "
                :src="item.dataUrl"
                class="w-full h-40 object-cover"
                muted
                playsinline
                preload="metadata"
              />
              <div
                v-else-if="item.fileType === ATTACHMENT_TYPES.AUDIO"
                class="w-full p-3"
              >
                <AudioChip
                  :attachment="item"
                  :show-transcribed-text="false"
                  class="bg-transparent border-0 shadow-none p-0"
                />
                <div
                  v-if="item.transcribedText"
                  class="mt-3 rounded-xl bg-n-surface-1 p-3"
                >
                  <TranscribedText :text="item.transcribedText" />
                </div>
              </div>
              <div
                v-else-if="
                  [ATTACHMENT_TYPES.FILE, ATTACHMENT_TYPES.FALLBACK].includes(
                    item.fileType
                  )
                "
                class="w-full p-3"
              >
                <div
                  class="flex w-full items-center gap-3 rounded-xl border border-n-weak bg-n-surface-1 p-3"
                >
                  <span
                    class="grid size-10 flex-shrink-0 place-content-center rounded-xl bg-n-alpha-2 text-n-slate-11"
                  >
                    <fluent-icon icon="document" size="18" />
                  </span>
                  <div class="min-w-0 flex-1">
                    <p class="mb-1 truncate text-sm font-medium text-n-slate-12">
                      {{ fileTitle(item) }}
                    </p>
                    <p class="mb-0 text-xs text-n-slate-10">
                      {{ mediaTypeLabel(item.fileType) }}
                    </p>
                  </div>
                  <a
                    v-if="item.dataUrl"
                    :href="item.dataUrl"
                    rel="noreferrer noopener nofollow"
                    target="_blank"
                    class="inline-flex items-center gap-1 rounded-lg border border-n-weak px-2.5 py-1.5 text-xs font-medium text-n-slate-12 transition hover:border-n-brand/40 hover:text-n-brand"
                    @click.stop
                  >
                    <fluent-icon icon="arrow-download" size="14" />
                    <span>{{ t('CONVERSATION.DOWNLOAD') }}</span>
                  </a>
                  <FileChip
                    v-else
                    :attachment="item"
                    class="!h-auto !min-h-12 !w-full"
                  />
                </div>
              </div>
              <div
                v-else
                class="flex flex-col items-center gap-2 text-n-slate-11"
              >
                <fluent-icon
                  :icon="
                    item.fileType === ATTACHMENT_TYPES.AUDIO
                      ? 'music-note-2'
                      : item.fileType === ATTACHMENT_TYPES.FILE
                        ? 'document'
                        : 'video'
                  "
                  size="28"
                />
                <span class="text-xs">{{ mediaTypeLabel(item.fileType) }}</span>
              </div>
            </div>

            <div class="flex flex-col gap-1 w-full min-w-0">
              <div class="flex w-full items-center justify-between gap-2">
                <span
                  class="inline-flex items-center gap-1 rounded-full border border-n-strong/60 bg-n-alpha-1 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-n-slate-11"
                >
                  <fluent-icon
                    :icon="
                      item.fileType === ATTACHMENT_TYPES.IMAGE
                        ? 'image'
                        : item.fileType === ATTACHMENT_TYPES.AUDIO
                          ? 'music-note-2'
                          : item.fileType === ATTACHMENT_TYPES.FILE ||
                              item.fileType === ATTACHMENT_TYPES.FALLBACK
                            ? 'document'
                            : 'video'
                    "
                    size="12"
                  />
                  {{ mediaTypeLabel(item.fileType) }}
                </span>
                <span class="whitespace-nowrap text-xs text-n-slate-11">
                  {{ dynamicTime(item.createdAt) }}
                </span>
              </div>

              <p class="m-0 truncate text-sm font-medium text-n-slate-12">
                {{ fileTitle(item) }}
              </p>

              <p class="m-0 truncate text-xs text-n-slate-11">
                {{ t('MONITOR_PAGE.GALLERY.BY', { name: item.senderName }) }}
              </p>

              <p class="m-0 truncate text-xs text-n-slate-10">
                {{
                  t('MONITOR_PAGE.GALLERY.CONVERSATION', {
                    id: item.conversationId,
                  })
                }}
              </p>

              <router-link
                :to="mediaUrl(item)"
                class="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-n-brand hover:underline"
              >
                <fluent-icon
                  :icon="isVisualMedia(item.fileType) ? 'image' : 'chat'"
                  size="14"
                />
                <span>{{ t('MONITOR_PAGE.GALLERY.OPEN_CONVERSATION') }}</span>
              </router-link>
            </div>
          </CardLayout>
        </div>
      </div>

      <div v-if="isFetching" class="flex justify-center py-6">
        <woot-loading-state :message="$t('SEARCH.LOADING_DATA')" />
      </div>

      <div v-if="showLoadMore" class="flex justify-center pt-4">
        <NextButton
          :label="t('MONITOR_PAGE.GALLERY.LOAD_MORE')"
          icon="i-lucide-cloud-download"
          slate
          sm
          faded
          @click="loadMore"
        />
      </div>
    </div>
  </div>
</template>
